import { useTranslation } from 'react-i18next';

import type { SYSTEM_ROLES } from '@constants';

import { MODULE } from '@modules/types';

import { usePermissions } from '@hooks';

const {
  BONUS,
  CAMPAIGN,
  CODE_CAMPAIGN,
  AUTOMATIC_CAMPAIGN,
  SCHEDULED_CAMPAIGN,
} = MODULE;

type ModuleWithDetailsView = Extract<
  MODULE,
  | 'bonus'
  | 'campaign'
  | 'codeCampaign'
  | 'automaticCampaign'
  | 'scheduledCampaign'
>;

interface IUseModeTitle {
  isEditMode: boolean;
  module: ModuleWithDetailsView;
  hasBrandsWithoutAccess: boolean;
}

const permissions: Record<
  Extract<
    MODULE,
    | 'bonus'
    | 'campaign'
    | 'codeCampaign'
    | 'automaticCampaign'
    | 'scheduledCampaign'
  >,
  SYSTEM_ROLES.PermissionModules
> = {
  [BONUS]: 'ext_bonus_system_bonuses',
  [CODE_CAMPAIGN]: 'bonus_system_code_campaigns',
  [CAMPAIGN]: 'ext_bonus_system_automatic_campaigns',
  [AUTOMATIC_CAMPAIGN]: 'ext_bonus_system_automatic_campaigns',
  [SCHEDULED_CAMPAIGN]: 'ext_bonus_system_scheduled_campaigns',
};

type TitleMode = 'Edit' | 'View' | 'Create';

enum TitleModeKey {
  CREATE = 'Create',
  EDIT = 'Edit',
  VIEW = 'View',
}

interface IUseModeTitleResult {
  title: string;
  isIframe: boolean;
  tooltipTitle: string;
}

export const useModeTitle = ({
  module,
  isEditMode,
  hasBrandsWithoutAccess,
}: IUseModeTitle): IUseModeTitleResult => {
  const isIframe = window.self !== window.top;
  const { t } = useTranslation();

  const isCampaignPage = [
    AUTOMATIC_CAMPAIGN,
    CAMPAIGN,
    CODE_CAMPAIGN,
    SCHEDULED_CAMPAIGN,
  ].includes(module);

  const modulePermissions = usePermissions(permissions[module]);

  const tooltipTitle = isCampaignPage
    ? 'common.button.copy.link.campaign.tooltip'
    : 'common.button.copy.link.bonus.tooltip';

  const getTitle = (mode: TitleMode) => {
    const translationKeys = {
      Edit: isCampaignPage
        ? 'campaign.automatic.details.title.edit'
        : 'bonus.details.title.edit',
      View: isCampaignPage
        ? 'campaign.automatic.details.title.create'
        : 'bonus.details.title.create',
      Create: isCampaignPage
        ? 'campaign.automatic.details.title.create'
        : 'bonus.details.title.create',
    };

    return t(translationKeys[mode]);
  };

  if (!isEditMode) {
    return { isIframe, tooltipTitle, title: getTitle(TitleModeKey.CREATE) };
  }

  const mode =
    modulePermissions?.hasEditPermission && !hasBrandsWithoutAccess
      ? TitleModeKey.EDIT
      : TitleModeKey.VIEW;

  return { isIframe, tooltipTitle, title: getTitle(mode) };
};
